import { FunctionComponent } from "react";
import { FormikValues } from "formik";
import { faArrowLeft, faArrowRight } from "@fortawesome/pro-solid-svg-icons";
import styles from "./stepped-drawer.module.scss";
import type {
    CustomSteppedDrawerFactory,
    IProps,
    ISteppedDrawerFormikProps,
    ISteppedDrawerHeaderProps,
} from "./stepped-drawer.types";
import { componentWrapper } from "../../utilities";
import { Drawer } from "../drawer";
import { Button } from "../button";
import { ACTION_DRAWER_OFFSET, ACTION_DRAWER_SIZE } from "../action-drawer/action-drawer.constants";
import { SteppedDrawerFooter } from "./stepped-drawer-footer";
import { DEFAULT_SUBMIT_BUTTON_TEXT } from "./stepped-drawer.constants";
import {
    clampStepIndex,
    getFooter,
    getSteps,
    SteppedFlowFormikForm,
    SteppedFlowFormikStep,
    SteppedFlowStep,
    SteppedFlowStepper,
    useSteppedFlowFormik,
} from "../stepped-flow";

/**
 * Shared drawer header for both the base and Formik SteppedDrawer variants: the step indicator and
 * the close button. Clicking a previous step navigates back; future steps are not clickable.
 */
const SteppedDrawerHeader: FunctionComponent<ISteppedDrawerHeaderProps> = ({
    active,
    onStepChange,
    steps,
    closeButtonDisabled = false,
}) => (
    <Drawer.Header className={styles["header"]}>
        <SteppedFlowStepper active={active} onStepChange={onStepChange} steps={steps} />
        <Drawer.CloseButton disabled={closeButtonDisabled} />
    </Drawer.Header>
);

/**
 * Drawer with multi-step navigation, the drawer counterpart of SteppedModal. The header shows a step
 * indicator (numbered pills); only the active step's content is rendered in the scrolling body. Fully
 * controlled via `active`/`onStepChange`. Clicking a previous step navigates back; future steps are not
 * clickable. The footer is the consumer's, via SteppedDrawer.Footer.
 */
const SteppedDrawer = componentWrapper<CustomSteppedDrawerFactory, IProps>(
    "SteppedDrawer",
    ({ active, onStepChange, children, closeButtonDisabled, ariaLabel, ...otherProps }, ref) => {
        const steps = getSteps(children);
        const footer = getFooter(children, SteppedDrawerFooter);
        const clampedActive = clampStepIndex(active, steps.length);

        return (
            <Drawer.Root
                ref={ref}
                position="right"
                size={ACTION_DRAWER_SIZE}
                offset={ACTION_DRAWER_OFFSET}
                radius="lg"
                {...otherProps}
            >
                <Drawer.Overlay />
                <Drawer.Content aria-label={ariaLabel}>
                    <SteppedDrawerHeader
                        active={clampedActive}
                        onStepChange={onStepChange}
                        steps={steps}
                        closeButtonDisabled={closeButtonDisabled}
                    />
                    <Drawer.Body>{steps[clampedActive]}</Drawer.Body>
                    {footer}
                </Drawer.Content>
            </Drawer.Root>
        );
    }
);

/**
 * Formik-aware SteppedDrawer. It owns the form: pass the Formik config (initialValues, onSubmit, …) as
 * props and the SteppedDrawer.Formik.Step children as content, each with its own `validation` schema,
 * exactly as for SteppedModal.Formik. Next only checks the active step's schema; Back never validates.
 * The footer is built in: Cancel on the first step and Back after it, Next labelled with the next
 * step's name, and a submit button on the final step whose label and props may depend on the form's
 * values. The drawer cannot be dismissed while the form is submitting.
 */
const SteppedDrawerFormik = <Values extends FormikValues = FormikValues>({
    active,
    onStepChange,
    children,
    submitButtonText = DEFAULT_SUBMIT_BUTTON_TEXT,
    submitButtonProps,
    hideSubmitButton = false,
    submitButtonDisabled = false,
    initialValues,
    onSubmit,
    enableReinitialize,
    validateOnMount,
    ariaLabel,
    ...drawerProps
}: ISteppedDrawerFormikProps<Values>) => {
    const {
        steps,
        active: clampedActive,
        isFinalStep,
        formik,
        isSubmitting,
        isValid,
        isActiveStepValid,
        isValidationReady,
        formState,
    } = useSteppedFlowFormik<Values>({ active, children, stepName: "SteppedDrawer.Formik.Step" });
    const nextButtonText = steps[clampedActive]?.props.nextButtonText ?? steps[clampedActive + 1]?.props.label;

    // Until the form has mounted, the function forms have no form state to work from, so the defaults apply.
    const resolvedSubmitButtonText =
        typeof submitButtonText === "function"
            ? formik === null
                ? DEFAULT_SUBMIT_BUTTON_TEXT
                : submitButtonText(formik)
            : submitButtonText;
    const resolvedSubmitButtonProps =
        typeof submitButtonProps === "function"
            ? formik === null
                ? undefined
                : submitButtonProps(formik)
            : submitButtonProps;

    return (
        <Drawer.Root
            position="right"
            size={ACTION_DRAWER_SIZE}
            offset={ACTION_DRAWER_OFFSET}
            radius="lg"
            closeOnClickOutside={!isSubmitting}
            closeOnEscape={!isSubmitting}
            {...drawerProps}
        >
            <Drawer.Overlay />
            <Drawer.Content aria-label={ariaLabel}>
                <SteppedDrawerHeader
                    active={clampedActive}
                    onStepChange={(step) => {
                        // Locked like the footer's Back while submitting, so no field remounts mid-submit.
                        if (!isSubmitting) {
                            onStepChange(step);
                        }
                    }}
                    steps={steps}
                    closeButtonDisabled={isSubmitting}
                />
                <Drawer.Body>
                    <SteppedFlowFormikForm<Values>
                        {...formState}
                        initialValues={initialValues}
                        onSubmit={onSubmit}
                        enableReinitialize={enableReinitialize}
                        validateOnMount={validateOnMount}
                    />
                </Drawer.Body>
                <SteppedDrawerFooter>
                    {clampedActive === 0 ? (
                        <Drawer.Footer.Actions.Cancel
                            onClick={drawerProps.onClose}
                            disabled={isSubmitting}
                            data-testid="stepped-drawer-cancel-button"
                        />
                    ) : (
                        <Button
                            variant="default"
                            icon={faArrowLeft}
                            onClick={() => onStepChange(clampedActive - 1)}
                            disabled={isSubmitting}
                            data-testid="stepped-drawer-back-button"
                        >
                            Back
                        </Button>
                    )}
                    {/* With zero recognised steps, `isFinalStep` (`0 === -1`) is false, so without this guard
                        an empty-labelled Next would render for a drawer with no SteppedDrawer.Formik.Step
                        children. */}
                    {!isFinalStep && steps.length > 0 && (
                        <Button
                            variant="filled"
                            onClick={() => onStepChange(clampedActive + 1)}
                            disabled={formik === null || !isActiveStepValid || isSubmitting}
                            data-testid="stepped-drawer-next-button"
                            rightIcon={faArrowRight}
                        >
                            {nextButtonText}
                        </Button>
                    )}
                    {isFinalStep && !hideSubmitButton && (
                        <Drawer.Footer.Actions.Done
                            data-testid="stepped-drawer-submit-button"
                            {...resolvedSubmitButtonProps}
                            text={resolvedSubmitButtonText}
                            onClick={() => formik?.submitForm()}
                            loading={isSubmitting}
                            disabled={
                                formik === null ||
                                !isValid ||
                                isSubmitting ||
                                submitButtonDisabled ||
                                !isValidationReady
                            }
                        />
                    )}
                </SteppedDrawerFooter>
            </Drawer.Content>
        </Drawer.Root>
    );
};

SteppedDrawer.Step = SteppedFlowStep;
SteppedDrawer.Footer = SteppedDrawerFooter;
SteppedDrawer.Formik = SteppedDrawerFormik;

// Attached after declaration rather than inline because SteppedDrawerFormik is a generic function
// component, so it cannot carry statics in its own type annotation.
SteppedDrawerFormik.Step = SteppedFlowFormikStep;

export { SteppedDrawer, SteppedDrawerFormik };
